import React, { useState, useEffect, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { CustomerFormModal } from 'components/modals/modules/customer-module/CustomerRegistryModal';
import type { CustomerFormData } from 'components/modals/modules/customer-module/CustomerRegistryModal';
import { useCustomers, useCreateCustomer, useGoldRates, useSystemConfig, useEnumsByGroup } from 'hooks/api';
import { ReturnedHistorySection } from 'components/common/ReturnedHistorySection';
import { approvalService } from 'api/services/approvalService';

// ============================================================================
// Types
// ============================================================================

export interface GLEntryFormData {
  id: string;
  glCustomerId: string;
  glCustomerName: string;
  glNumber: string;
  netWeight: string;
  originalLoanAmount: string;
  /** CR-002: outstanding GL principal — required on the Gross (FULL) rate basis; defaults from original amount */
  outstandingAmount: string;
  /** CR-001: optional per-GL documentation charge, deducted from disbursement (cash-flow only) */
  documentationCharge: string;
  loanDate: string;
}

export interface AddonRequestFormData {
  loanType: string;
  loanAccountNumber: string;
  addonCustomerId: string;
  addonCustomerName: string;
  requestedAmount: string;
  glEntries: GLEntryFormData[];
}

export interface AddonRequestRecord {
  id: string;
  addonCustomerId: string;
  customerName: string;
  customerId?: string;
  addonLoanType: string;
  addonLoanAccountNumber: string;
  requestedAmount: number;
  statusCode: string;
  statusName?: string;
  goldRateId: string;
  ratePerGram?: number;
  /** CR-002: basis of the applied gold rate (NET legacy / FULL gross) */
  rateBasis?: string;
  grossValue: number;
  eligibleAmount: number;
  maximumPermissible: number;
  glEntries: {
    id: string;
    customerId: string;
    customerName?: string;
    glNumber: string;
    netWeight: number;
    originalLoanAmount: number;
    outstandingAmount?: number | null;
    documentationCharge?: number;
    disbursementAmount?: number | null;
    loanDate: string;
  }[];
  submittedBy?: string;
  submittedByName?: string;
  submittedAt?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  rowVersion: number;
  businessUnitName?: string;
}

// Loan type options are fetched dynamically via useEnumsByGroup('LOAN_TYPE') inside the form modal

// ============================================================================
// ELIGIBILITY CALCULATOR
// ============================================================================

// CR-002: basis-aware. NET (legacy) applies the rate directly; FULL (gross) computes the
// gross gold value and deducts the keyed-in outstanding GL principal.
const calculateEligibility = (
  glEntries: GLEntryFormData[],
  goldRate: number,
  rateBasis: string,
  roundingUnit: number,
) => {
  const totalNetWeight = glEntries.reduce((sum, gl) => sum + (parseFloat(gl.netWeight) || 0), 0);
  const totalOutstanding = glEntries.reduce((sum, gl) => sum + (parseFloat(gl.outstandingAmount) || 0), 0);
  const isGross = rateBasis === 'FULL';

  const grossValue = Math.round(totalNetWeight * goldRate);
  const eligibleAmount = isGross ? grossValue - totalOutstanding : grossValue;
  const maximumPermissible = eligibleAmount > 0
    ? Math.ceil(eligibleAmount / roundingUnit) * roundingUnit
    : 0;
  const missingOutstanding = isGross
    ? glEntries.some((gl) => (parseFloat(gl.netWeight) || 0) > 0 && !(parseFloat(gl.outstandingAmount) > 0))
    : false;

  return { totalNetWeight, grossValue, totalOutstanding, eligibleAmount, maximumPermissible, isGross, missingOutstanding };
};

// ============================================================================
// AMOUNT INDICATOR COLOR
// ============================================================================

const getAmountIndicator = (amount: number, eligible: number, maxPermissible: number): { color: string; label: string } => {
  if (amount <= 0 || maxPermissible <= 0) return { color: 'var(--text-muted)', label: '' };
  if (amount <= eligible) return { color: '#16a34a', label: 'Within Eligible' };
  if (amount <= maxPermissible) return { color: '#d97706', label: 'Between Eligible & Max' };
  return { color: '#dc2626', label: 'Exceeds Maximum' };
};

// ============================================================================
// SECTION HEADER COMPONENT
// ============================================================================

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div style={{
    padding: '0.75rem 0',
    marginBottom: '0.75rem',
    borderBottom: '2px solid var(--primary)',
  }}>
    <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
    {subtitle && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>{subtitle}</p>}
  </div>
);

// ============================================================================
// Form Modal (Create / Edit) — Glassmorphism Design
// ============================================================================

interface AddonRequestFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: AddonRequestFormData;
  onFormDataChange: (data: AddonRequestFormData) => void;
  onSave: () => void;
  onSubmit?: () => void;
  onClose: () => void;
}

export const AddonRequestFormModal: React.FC<AddonRequestFormModalProps> = ({
  isOpen,
  mode,
  formData,
  onFormDataChange,
  onSave,
  onSubmit,
  onClose,
}) => {
  // Customer Registry modal state (hooks must be before any early return)
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerModalTarget, setCustomerModalTarget] = useState<{ type: 'section-a' } | { type: 'section-b'; index: number }>({ type: 'section-a' });
  const [customerFormData, setCustomerFormData] = useState<CustomerFormData>({ customerId: '', customerName: '' });

  // API data hooks (must be before early return)
  const { data: customers, refetch: refetchCustomers } = useCustomers();
  const { createCustomer } = useCreateCustomer();
  const { data: goldRates } = useGoldRates();
  const { data: systemConfigs } = useSystemConfig();
  const { data: loanTypeEnums } = useEnumsByGroup('LOAN_TYPE');

  // Build loan type dropdown options from API enum data
  const loanTypeOptions = useMemo(() =>
    loanTypeEnums.map(e => ({ value: e.enumKey, label: e.enumValue })),
    [loanTypeEnums]
  );

  // Build customer dropdown options and name lookup from API data
  // Dropdown value = UUID (c.id) so backend receives the correct FK
  const customerOptions = useMemo(() =>
    customers.map(c => ({ value: String(c.id), label: `${c.customerId} - ${c.customerName}` })),
    [customers]
  );
  const customerNameLookup = useMemo(() => {
    const map: Record<string, string> = {};
    customers.forEach(c => { map[String(c.id)] = c.customerName; });
    return map;
  }, [customers]);

  // Get active gold rate and rounding unit from API
  const activeGoldRate = useMemo(() => goldRates.find(r => r.isActive)?.ratePerGram ?? 0, [goldRates]);
  const activeGoldRateBasis = useMemo(() => goldRates.find(r => r.isActive)?.rateBasis ?? 'NET', [goldRates]);
  const roundingUnit = useMemo(() => {
    const cfg = systemConfigs.find(c => c.configKey === 'rounding_unit');
    return cfg ? parseInt(cfg.configValue, 10) || 5000 : 5000;
  }, [systemConfigs]);

  if (!isOpen) return null;

  const title = mode === 'create' ? 'New Add-On Request' : 'Edit Add-On Request';

  const handleFieldChange = (field: keyof Omit<AddonRequestFormData, 'glEntries'>, value: string) => {
    const updated = { ...formData, [field]: value };
    // Auto-populate customer name and default GL Customer IDs
    if (field === 'addonCustomerId') {
      updated.addonCustomerName = customerNameLookup[value] || '';
      // Default GL Customer ID for entries that are still empty
      if (value) {
        updated.glEntries = updated.glEntries.map((gl) =>
          gl.glCustomerId ? gl : { ...gl, glCustomerId: value, glCustomerName: customerNameLookup[value] || '' }
        );
      }
    }
    onFormDataChange(updated);
  };

  const handleOpenCustomerModal = (target: typeof customerModalTarget) => {
    setCustomerModalTarget(target);
    setCustomerFormData({ customerId: '', customerName: '' });
    setCustomerModalOpen(true);
  };

  const handleSaveNewCustomer = async () => {
    const { customerId, customerName } = customerFormData;
    if (!customerId.trim() || !customerName.trim()) return;
    const cleanId = customerId.toUpperCase().replace(/\s/g, '');
    const trimmedName = customerName.trim();
    try {
      const created = await createCustomer({
        customerId: cleanId,
        customerName: trimmedName,
      });
      const uuid = String(created.id);
      await refetchCustomers();
      // Set customer directly (customerNameLookup is stale until next render)
      if (customerModalTarget.type === 'section-a') {
        const updated = { ...formData, addonCustomerId: uuid, addonCustomerName: trimmedName };
        updated.glEntries = updated.glEntries.map((gl) =>
          gl.glCustomerId ? gl : { ...gl, glCustomerId: uuid, glCustomerName: trimmedName }
        );
        onFormDataChange(updated);
      } else {
        const updatedGLs = [...formData.glEntries];
        updatedGLs[customerModalTarget.index] = {
          ...updatedGLs[customerModalTarget.index],
          glCustomerId: uuid,
          glCustomerName: trimmedName,
        };
        const updated = { ...formData, glEntries: updatedGLs };
        if (customerModalTarget.index === 0 && !formData.addonCustomerId) {
          updated.addonCustomerId = uuid;
          updated.addonCustomerName = trimmedName;
        }
        onFormDataChange(updated);
      }
      setCustomerModalOpen(false);
    } catch (err: any) {
      const detail = err?.response?.data;
      alert(detail?.error?.message || detail?.message || (typeof detail === 'string' ? detail : null) || err?.message || 'Failed to create customer');
    }
  };

  const handleGLChange = (index: number, field: keyof GLEntryFormData, value: string) => {
    const updatedGLs = [...formData.glEntries];
    const previous = updatedGLs[index];
    updatedGLs[index] = { ...previous, [field]: value };
    // CR-002 Loan Base convention: outstanding defaults from the original amount until edited independently
    if (field === 'originalLoanAmount' &&
        (!previous.outstandingAmount || previous.outstandingAmount === previous.originalLoanAmount)) {
      updatedGLs[index].outstandingAmount = value;
    }
    // Auto-populate GL customer name
    if (field === 'glCustomerId') {
      updatedGLs[index].glCustomerName = customerNameLookup[value] || '';
      // Auto-fill Section A customer from first GL if empty
      if (index === 0 && !formData.addonCustomerId) {
        onFormDataChange({
          ...formData,
          addonCustomerId: value,
          addonCustomerName: customerNameLookup[value] || '',
          glEntries: updatedGLs,
        });
        return;
      }
    }
    onFormDataChange({ ...formData, glEntries: updatedGLs });
  };

  const handleAddGL = () => {
    const firstGL = formData.glEntries[0];
    const newGL: GLEntryFormData = {
      id: `GL_${Date.now()}`,
      glCustomerId: firstGL?.glCustomerId || '',
      glCustomerName: firstGL?.glCustomerName || '',
      glNumber: '',
      netWeight: '',
      originalLoanAmount: '',
      outstandingAmount: '',
      documentationCharge: '',
      loanDate: new Date().toISOString().split('T')[0],
    };
    onFormDataChange({ ...formData, glEntries: [...formData.glEntries, newGL] });
  };

  const handleRemoveGL = (index: number) => {
    if (formData.glEntries.length <= 1) return;
    const updatedGLs = formData.glEntries.filter((_, i) => i !== index);
    onFormDataChange({ ...formData, glEntries: updatedGLs });
  };

  // Calculations
  const calc = calculateEligibility(formData.glEntries, activeGoldRate, activeGoldRateBasis, roundingUnit);
  const requestedAmt = parseFloat(formData.requestedAmount) || 0;
  const indicator = getAmountIndicator(requestedAmt, calc.eligibleAmount, calc.maximumPermissible);
  const exceedsMax = requestedAmt > 0 && calc.maximumPermissible > 0 && requestedAmt > calc.maximumPermissible;
  // CR-002: on the gross basis a zero/negative eligibility blocks the request entirely
  const eligibilityBlocked = calc.isGross && calc.totalNetWeight > 0 && calc.eligibleAmount <= 0;

  // 14-character length validation
  const loanAccNumError =
    formData.loanAccountNumber && formData.loanAccountNumber.length !== 14
      ? 'Must be exactly 14 characters'
      : '';
  const glNumberErrors = formData.glEntries.map((gl) =>
    gl.glNumber && gl.glNumber.length !== 14 ? 'Must be exactly 14 characters' : ''
  );
  const hasAccountErrors = !!loanAccNumError || glNumberErrors.some(Boolean);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* ========== SECTION A: Add-On Loan Details ========== */}
          <SectionHeader title="A. Add-On Loan Details" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <SyutSelect
                label="Loan Type *"
                options={loanTypeOptions}
                value={formData.loanType}
                onChange={(val) => handleFieldChange('loanType', (val as string) || '')}
                placeholder="Select Loan Type..."
                isSearchable
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Loan Account Number * <span style={{ fontWeight: 400, fontSize: '0.7rem', color: 'var(--text-muted)' }}>(14 characters)</span></label>
              <input
                type="text"
                className="form-input"
                value={formData.loanAccountNumber}
                onChange={(e) => handleFieldChange('loanAccountNumber', e.target.value.replace(/\s/g, ''))}
                placeholder="14-character account number"
                maxLength={14}
                style={{ border: `1px solid ${loanAccNumError ? 'var(--danger, #dc2626)' : '#334155'}` }}
              />
              {loanAccNumError && (
                <span style={{ fontSize: '0.7rem', color: 'var(--danger, #dc2626)', marginTop: '0.2rem', display: 'block' }}>
                  {loanAccNumError}
                </span>
              )}
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <SyutSelect
                    label="Customer ID *"
                    options={customerOptions}
                    value={formData.addonCustomerId}
                    onChange={(val) => handleFieldChange('addonCustomerId', (val as string) || '')}
                    placeholder="Select Customer ID..."
                    isSearchable
                    isClearable={false}
                    size="sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleOpenCustomerModal({ type: 'section-a' })}
                  title="Add New Customer"
                  style={{
                    background: 'transparent', color: 'var(--text-secondary)', border: '1px solid #334155', borderRadius: '0.375rem',
                    width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = '#334155'; }}
                >
                  <IconSystem.add size={16} />
                </button>
              </div>
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Customer Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.addonCustomerName}
                readOnly
                style={{ border: '1px solid #334155', background: 'var(--bg-secondary)', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          {/* ========== SECTION B: Gold Loans ========== */}
          <SectionHeader title="B. Gold Loans" subtitle="Add gold loan entries pledged as collateral" />
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)' }}>#</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)', minWidth: '300px' }}>GL Customer ID</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)', minWidth: '120px' }}>GL Number</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)', minWidth: '100px' }}>Eligible Weight (g)</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)', minWidth: '130px' }}>Original Amount (₹)</th>
                    {calc.isGross && (
                      <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)', minWidth: '130px' }}>Outstanding (₹) *</th>
                    )}
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)', minWidth: '120px' }}>Doc Charge (₹)</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)', minWidth: '120px' }}>Disbursement (₹)</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)', minWidth: '120px' }}>Loan Date</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border-color)', width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.glEntries.map((gl, idx) => (
                    <tr key={gl.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.375rem 0.5rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '0.375rem 0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <div style={{ flex: 1 }}>
                            <SyutSelect
                              options={customerOptions}
                              value={gl.glCustomerId}
                              onChange={(val) => handleGLChange(idx, 'glCustomerId', (val as string) || '')}
                              placeholder="Select..."
                              isSearchable
                              isClearable={false}
                              size="sm"
                              menuPlacement="auto"
                              menuPortalTarget={document.body}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenCustomerModal({ type: 'section-b', index: idx })}
                            title="Add New Customer"
                            style={{
                              background: 'transparent', color: 'var(--text-secondary)', border: '1px solid #334155', borderRadius: '0.25rem',
                              width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = '#334155'; }}
                          >
                            <IconSystem.add size={12} />
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem' }}>
                        <input
                          type="text"
                          className="form-input"
                          value={gl.glNumber}
                          onChange={(e) => handleGLChange(idx, 'glNumber', e.target.value)}
                          placeholder="14-char GL Number"
                          maxLength={14}
                          style={{
                            fontSize: '0.8rem', padding: '0.25rem 0.5rem',
                            border: `1px solid ${glNumberErrors[idx] ? 'var(--danger, #dc2626)' : '#334155'}`,
                          }}
                        />
                        {glNumberErrors[idx] && (
                          <div style={{ fontSize: '0.68rem', color: 'var(--danger, #dc2626)', marginTop: '0.1rem', whiteSpace: 'nowrap' }}>
                            {glNumberErrors[idx]}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem' }}>
                        <input
                          type="number"
                          className="form-input"
                          value={gl.netWeight}
                          onChange={(e) => handleGLChange(idx, 'netWeight', e.target.value)}
                          placeholder="0.000"
                          min={0}
                          step="0.001"
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', textAlign: 'right', border: '1px solid #334155' }}
                        />
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem' }}>
                        <input
                          type="number"
                          className="form-input"
                          value={gl.originalLoanAmount}
                          onChange={(e) => handleGLChange(idx, 'originalLoanAmount', e.target.value)}
                          placeholder="0.00"
                          min={0}
                          step="0.01"
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', textAlign: 'right', border: '1px solid #334155' }}
                        />
                      </td>
                      {calc.isGross && (
                        <td style={{ padding: '0.375rem 0.5rem' }}>
                          <input
                            type="number"
                            className="form-input"
                            value={gl.outstandingAmount}
                            onChange={(e) => handleGLChange(idx, 'outstandingAmount', e.target.value)}
                            placeholder="0.00"
                            min={0}
                            step="0.01"
                            title="Outstanding GL principal — deducted from gross gold value"
                            style={{
                              fontSize: '0.8rem', padding: '0.25rem 0.5rem', textAlign: 'right',
                              border: `1px solid ${(parseFloat(gl.netWeight) || 0) > 0 && !(parseFloat(gl.outstandingAmount) > 0) ? '#f59e0b' : '#334155'}`,
                            }}
                          />
                        </td>
                      )}
                      <td style={{ padding: '0.375rem 0.5rem' }}>
                        <input
                          type="number"
                          className="form-input"
                          value={gl.documentationCharge}
                          onChange={(e) => handleGLChange(idx, 'documentationCharge', e.target.value)}
                          placeholder="0.00"
                          min={0}
                          step="0.01"
                          title="Optional — deducted from disbursement; does not affect eligibility"
                          style={{
                            fontSize: '0.8rem', padding: '0.25rem 0.5rem', textAlign: 'right',
                            border: `1px solid ${(parseFloat(gl.documentationCharge) || 0) > 0 && (parseFloat(gl.documentationCharge) || 0) >= (parseFloat(gl.originalLoanAmount) || 0) ? 'var(--danger, #dc2626)' : '#334155'}`,
                          }}
                        />
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>
                        {formatCurrency(Math.max(0, (parseFloat(gl.originalLoanAmount) || 0) - (parseFloat(gl.documentationCharge) || 0)))}
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem' }}>
                        <input
                          type="date"
                          className="form-input"
                          value={gl.loanDate}
                          onChange={(e) => handleGLChange(idx, 'loanDate', e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', border: '1px solid #334155' }}
                        />
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem', textAlign: 'center' }}>
                        {formData.glEntries.length > 1 && (
                          <button
                            onClick={() => handleRemoveGL(idx)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: 'var(--danger)', padding: '0.25rem',
                            }}
                            title="Remove GL"
                          >
                            <IconSystem.trash size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              className="btn btn-secondary"
              onClick={handleAddGL}
              style={{ marginTop: '0.75rem', fontSize: '0.8rem', padding: '0.375rem 0.75rem' }}
            >
              <IconSystem.add size={14} />
              Add Gold Loan
            </button>
          </div>

          {/* ========== SECTION C: Eligibility Summary ========== */}
          <SectionHeader title="C. Eligibility Summary" subtitle="Auto-calculated based on gold loan entries" />
          {mode === 'edit' && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
              background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.375rem', color: '#1e40af', fontSize: '0.75rem' }}>
              <IconSystem.info size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>
                Eligibility is recalculated with the currently active gold rate
                ({formatCurrency(activeGoldRate)}/g, {calc.isGross ? 'Gross' : 'Net'} basis) when this request is saved.
                Figures may differ from those stored when the draft was created.
              </span>
            </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
            background: 'var(--bg-secondary)',
            padding: '1rem',
            borderRadius: '0.5rem',
            border: '1px solid var(--border-color)',
          }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Total Eligible Weight</div>
              <div style={{ fontSize: '1rem', fontWeight: 600 }}>{calc.totalNetWeight.toFixed(3)} g</div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Active Gold Rate</div>
              <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                {formatCurrency(activeGoldRate)}/g
                <span style={{ fontSize: '0.65rem', fontWeight: 600, marginLeft: '0.4rem', padding: '0.05rem 0.4rem', borderRadius: '0.625rem',
                  background: calc.isGross ? '#dbeafe' : '#e2e8f0', color: calc.isGross ? '#1d4ed8' : '#475569' }}>
                  {calc.isGross ? 'Gross' : 'Net'}
                </span>
              </div>
            </div>
            {calc.isGross && (
              <>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Gross Gold Value</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600 }}>{formatCurrency(calc.grossValue)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Less: GL Outstanding</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#dc2626' }}>−{formatCurrency(calc.totalOutstanding)}</div>
                </div>
              </>
            )}
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Eligible Amount</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: calc.eligibleAmount > 0 ? '#16a34a' : '#dc2626' }}>
                {formatCurrency(calc.eligibleAmount)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Maximum Permissible</div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--primary)' }}>{formatCurrency(calc.maximumPermissible)}</div>
            </div>
          </div>

          {/* ========== SECTION D: Amount Required ========== */}
          <SectionHeader title="D. Amount Required" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Amount Required (₹) *</label>
              <input
                type="number"
                className="form-input"
                value={formData.requestedAmount}
                onChange={(e) => handleFieldChange('requestedAmount', e.target.value)}
                placeholder="0.00"
                min={0}
                step="0.01"
                style={{
                  border: `2px solid ${indicator.color}`,
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              />
              {indicator.label && (
                <span style={{ fontSize: '0.7rem', color: indicator.color, fontWeight: 600, marginTop: '0.25rem', display: 'block' }}>
                  {indicator.label}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: '1.25rem' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Max: <strong style={{ color: 'var(--primary)' }}>{formatCurrency(calc.maximumPermissible)}</strong>
              </span>
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div>
            {exceedsMax && (
              <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                Amount exceeds maximum permissible ({formatCurrency(calc.maximumPermissible)})
              </span>
            )}
            {!exceedsMax && eligibilityBlocked && (
              <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                Eligible amount is {formatCurrency(calc.eligibleAmount)} — GL outstanding equals or exceeds the gross gold value
              </span>
            )}
            {!exceedsMax && !eligibilityBlocked && calc.missingOutstanding && (
              <span style={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>
                Outstanding GL amount is required for every GL (gross rate basis)
              </span>
            )}
            {!exceedsMax && !eligibilityBlocked && !calc.missingOutstanding && hasAccountErrors && (
              <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                Account / GL numbers must be exactly 14 characters
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={onSave}
              disabled={exceedsMax || hasAccountErrors || eligibilityBlocked || calc.missingOutstanding}
              style={exceedsMax || hasAccountErrors || eligibilityBlocked || calc.missingOutstanding ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
              <IconSystem.save size={16} />
              Save as Draft
            </button>
            {onSubmit && (
              <button className="btn btn-primary" onClick={onSubmit}
                disabled={exceedsMax || hasAccountErrors || eligibilityBlocked || calc.missingOutstanding}
                style={{
                  background: '#16a34a',
                  borderColor: '#16a34a',
                  ...(exceedsMax || hasAccountErrors || eligibilityBlocked || calc.missingOutstanding ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                }}>
                <IconSystem.check size={16} />
                Submit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Customer Registry Modal */}
      <CustomerFormModal
        isOpen={customerModalOpen}
        mode="create"
        formData={customerFormData}
        onFormDataChange={setCustomerFormData}
        onSave={handleSaveNewCustomer}
        onClose={() => setCustomerModalOpen(false)}
      />
    </>
  );
};

// ============================================================================
// View Modal — Glassmorphism Design
// ============================================================================

interface AddonRequestViewModalProps {
  isOpen: boolean;
  record: AddonRequestRecord | null;
  onClose: () => void;
}

export const AddonRequestViewModal: React.FC<AddonRequestViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  const { data: loanTypeEnums } = useEnumsByGroup('LOAN_TYPE');
  const loanTypeLookup = useMemo(() => {
    const map: Record<string, string> = {};
    loanTypeEnums.forEach(e => { map[e.enumKey] = e.enumValue; });
    return map;
  }, [loanTypeEnums]);

  const [approvalInfo, setApprovalInfo] = useState<{
    approvedBy?: string; approvedAt?: string;
    rejectedBy?: string; rejectedAt?: string; rejectedRemark?: string | null;
  }>({});
  useEffect(() => {
    if (isOpen && record?.id) {
      setApprovalInfo({});
      approvalService.getHistory('addon_request', record.id)
        .then((res) => {
          const history = res.data ?? [];
          const approved = history.find(h => h.action?.toLowerCase() === 'approved');
          const rejected = history.find(h => h.action?.toLowerCase() === 'rejected');
          setApprovalInfo({
            approvedBy: approved?.actionedByName, approvedAt: approved?.actionedAt,
            rejectedBy: rejected?.actionedByName, rejectedAt: rejected?.actionedAt, rejectedRemark: rejected?.remark,
          });
        })
        .catch(() => {});
    } else {
      setApprovalInfo({});
    }
  }, [isOpen, record?.id]);

  if (!isOpen || !record) return null;

  const indicator = getAmountIndicator(record.requestedAmount, record.eligibleAmount, record.maximumPermissible);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            Add-On Request
            <span style={getStatusBadgeStyle(record.statusCode)}>{(record.statusCode ?? '').replace(/_/g, ' ')}</span>
          </h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Section 1: Request Details Card */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Request Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Loan Type</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{loanTypeLookup[record.addonLoanType] || record.addonLoanType}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Loan Account Number</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.addonLoanAccountNumber}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer ID</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.customerId || record.addonCustomerId}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer Name</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.customerName}</div>
              </div>
            </div>
          </div>

          {/* Section 2: Gold Loans Table */}
          <h3 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
            Gold Loans
          </h3>
          <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ background: 'var(--bg-tertiary)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>#</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>GL Customer</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>GL Number</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Eligible Weight (g)</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Original Amount</th>
                  {record.rateBasis === 'FULL' && (
                    <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Outstanding</th>
                  )}
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Doc Charge</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Disbursement</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Loan Date</th>
                </tr>
              </thead>
              <tbody>
                {record.glEntries.map((gl, idx) => (
                  <tr key={gl.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '0.5rem' }}>{gl.customerName || gl.customerId}</td>
                    <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{gl.glNumber}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{gl.netWeight.toFixed(3)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(gl.originalLoanAmount)}</td>
                    {record.rateBasis === 'FULL' && (
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{gl.outstandingAmount != null ? formatCurrency(gl.outstandingAmount) : '—'}</td>
                    )}
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(gl.documentationCharge ?? 0)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(gl.disbursementAmount ?? Math.max(0, gl.originalLoanAmount - (gl.documentationCharge ?? 0)))}</td>
                    <td style={{ padding: '0.5rem' }}>{formatDate(gl.loanDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Section 3: Eligibility Summary Card */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Eligibility Summary
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Gold Rate</div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(record.ratePerGram ?? 0)}/g
                  {record.rateBasis && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 600, marginLeft: '0.4rem', padding: '0.05rem 0.4rem', borderRadius: '0.625rem',
                      background: record.rateBasis === 'FULL' ? '#dbeafe' : '#e2e8f0', color: record.rateBasis === 'FULL' ? '#1d4ed8' : '#475569' }}>
                      {record.rateBasis === 'FULL' ? 'Gross' : 'Net'}
                    </span>
                  )}
                </div>
              </div>
              {record.rateBasis === 'FULL' && (
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Gross Gold Value</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(record.grossValue)}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Eligible Amount</div>
                <div style={{ fontWeight: 600, color: record.eligibleAmount > 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(record.eligibleAmount)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Max Permissible</div>
                <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatCurrency(record.maximumPermissible)}</div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Requested Amount</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: indicator.color }}>{formatCurrency(record.requestedAmount)}</div>
              </div>
              {indicator.label && (
                <span style={{ fontSize: '0.75rem', color: indicator.color, fontWeight: 600 }}>
                  {indicator.label}
                </span>
              )}
            </div>
          </div>

          {/* Section 4: Submission & Approval Info Card */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Submission & Approval Info
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created By</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.createdByName || record.createdBy || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created At</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{formatDate(record.createdAt)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted By</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.submittedByName || record.submittedBy || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted At</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.submittedAt ? formatDate(record.submittedAt) : '-'}</div>
              </div>
            </div>
            {approvalInfo.approvedBy && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Approved By</div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#16a34a' }}>{approvalInfo.approvedBy}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Approved At</div>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{approvalInfo.approvedAt ? formatDate(approvalInfo.approvedAt) : '-'}</div>
                </div>
              </div>
            )}
            {approvalInfo.rejectedBy && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejected By</div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#dc2626' }}>{approvalInfo.rejectedBy}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejected At</div>
                    <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{approvalInfo.rejectedAt ? formatDate(approvalInfo.rejectedAt) : '-'}</div>
                  </div>
                </div>
                {approvalInfo.rejectedRemark && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejection Remark</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{approvalInfo.rejectedRemark}</div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Section 5: Workflow Information — Returned History
              Shown for RETURNED status (active return cycle) AND for REJECTED records
              that came from a prior return, so the return trail stays visible after rejection. */}
          {(record.statusCode === 'RETURNED' || approvalInfo.rejectedBy) && (
            <ReturnedHistorySection recordType="addon_request" recordId={record.id} />
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};
